import { useState, type ReactNode } from 'react'
import { cn } from '../../lib/cn'

/**
 * **Erst der Wert, auf Klick das Bedienelement** (Nils, 19.08.2026: *„Hier bitte die
 * DropDowns erst auf Klick editierbar machen"*).
 *
 * ## Warum das mehr ist als Geschmack
 *
 * Ein Aufgabendetail ist zuerst eine **Auskunft**: wer, woher, bis wann. Sechs dauerhaft
 * aufgeklappte Auswahlfelder untereinander lesen sich dagegen wie ein Formular, das
 * ausgefüllt werden will – jedes zieht Aufmerksamkeit auf sich, obwohl neunmal von zehn
 * niemand etwas ändern möchte. Der Rahmen um ein Feld ist eine Aufforderung; er gehört
 * dorthin, wo gerade gehandelt wird, und sonst nirgendwo.
 *
 * Der Preis wäre normalerweise ein zusätzlicher Klick. Den zahlt hier niemand: beim Öffnen
 * ruft `AufklappSelect` `showPicker()`, die Liste steht also nach demselben einen Klick
 * offen, mit dem man vorher das geschlossene Feld angeklickt hätte.
 *
 * ## Was „fertig" bedeutet
 *
 * Zurück in die Lesefassung. Ausgelöst wird es von drei Seiten: vom Bedienelement selbst
 * (Auswahl getroffen), von der Tastatur (Esc) und vom Fokus, der den Bereich verlässt.
 * Letzteres prüft `relatedTarget` – ohne das schlösse sich ein Editor mit mehreren Knöpfen
 * (Pools, Fälligkeit mit „löschen") beim Sprung von einem Knopf zum nächsten.
 */
export function InlineWert({
  wert,
  leer,
  darfAendern,
  bezeichnung,
  children,
}: {
  /** Was dasteht, solange niemand ändert. `null` heißt: nichts gesetzt. */
  wert: ReactNode
  /** Der blasse Text für „nichts gesetzt" – anklickbar bleibt er trotzdem. */
  leer?: string
  darfAendern: boolean
  /** Für den Titel des Knopfes: „Bearbeiter ändern". */
  bezeichnung: string
  children: (fertig: () => void) => ReactNode
}) {
  const [offen, setOffen] = useState(false)
  const schliessen = () => setOffen(false)

  if (offen && darfAendern) {
    return (
      <div
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) schliessen()
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') schliessen()
        }}
      >
        {children(schliessen)}
      </div>
    )
  }

  const anzeige = wert ?? <span className="text-muted">{leer ?? '–'}</span>
  if (!darfAendern) return <>{anzeige}</>

  return (
    <button
      type="button"
      onClick={() => setOffen(true)}
      title={`${bezeichnung} ändern`}
      className="group -mx-1 flex w-full cursor-pointer items-baseline gap-1 rounded-md px-1 py-px text-left hover:bg-[#f3f2ef]"
    >
      <span className="min-w-0 break-words">{anzeige}</span>
      {/**
       * **Der Hinweis steht im Text, nicht nur im `title`.** Ein Feld, das nur beim
       * Darüberfahren verrät, dass es eines ist, findet niemand mit der Tastatur – und der
       * zugängliche Name des Knopfes lautet so „Christian Hunhoff ändern" statt bloß
       * „Christian Hunhoff", was ihn von einer Textzeile unterscheidbar macht.
       */}
      <span className="ml-auto shrink-0 text-[10.5px] text-muted opacity-0 group-hover:opacity-100 group-focus:opacity-100">
        ändern
      </span>
    </button>
  )
}

/**
 * **Ein Auswahlfeld, das sich beim Öffnen auch öffnet.**
 *
 * `showPicker()` ist der Unterschied zwischen „klick, klick, wähle" und „klick, wähle". Wo es
 * fehlt – ältere Browser, fehlende Nutzergeste – bleibt ein normal fokussiertes Auswahlfeld
 * stehen; das ist genau der Zustand von vorher und kein Fehler, deshalb wird der Wurf
 * geschluckt.
 */
export function AufklappSelect({
  wert,
  setzen,
  fertig,
  disabled,
  ariaLabel,
  className,
  children,
}: {
  wert: string
  setzen: (wert: string) => void
  fertig: () => void
  disabled?: boolean
  ariaLabel: string
  className?: string
  children: ReactNode
}) {
  return (
    <select
      autoFocus
      ref={(el) => {
        try {
          ;(el as (HTMLSelectElement & { showPicker?: () => void }) | null)?.showPicker?.()
        } catch {
          /* Kein showPicker: das Feld ist fokussiert, ein Klick öffnet es. */
        }
      }}
      value={wert}
      disabled={disabled}
      aria-label={ariaLabel}
      onChange={(e) => {
        setzen(e.target.value)
        fertig()
      }}
      className={cn(
        'w-full cursor-pointer rounded-md border border-line2 bg-surface px-1.5 py-0.5 text-[12px]',
        className,
      )}
    >
      {children}
    </select>
  )
}
